import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import styles from './AdminBillingInfo.module.css';
import TextCard from '../../common/textCard/TextCard';
import Button from '../../common/button/Button';
import {AdminPaymentResponseData} from '../../../types/admin';
import {useAlert} from '../../common/error/AlertContext';
import {cancelPaymentAPI, getPaymentAPI} from "../../../services/admin";
import CancelPaymentPopUp from "../../../views/admin/popup/CancelPaymentPopUp";

interface AdminBillingInfoProps {
    paymentId: number;
}

//결제 상세 정보
const AdminBillingInfo: React.FC<AdminBillingInfoProps> = ({paymentId}) => {
    const {id} = useParams();
    const [data, setData] = useState<AdminPaymentResponseData | null>(null);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const {showAlert} = useAlert();

    //결제 취소
    const handleCancelClick = () => {
        setIsCancelModalOpen(true);
    };

    //데이터 세팅
    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await getPaymentAPI(paymentId);
            setData(response);
        } catch (error) {
            console.error('Error fetching payment info:', error);
            showAlert('결제 정보를 불러오는데 실패했습니다.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    //결제 취소 로직
    const handleCancelConfirm = async (reason: string, amount?: number) => {
        try {
            if (!data?.paymentKey) return; //토스 페이먼츠 사용위한 paymentKey 없는 경우 호출 불가
            //결제 취소 API
            await cancelPaymentAPI({
                paymentId: paymentId,
                cancelReason: reason,
                cancelAmount: amount || data.amount
            })
            showAlert('결제가 취소되었습니다.', 'info'); //알림창
            setIsCancelModalOpen(false); //모달 닫기
            fetchData();
        } catch (error) {
            console.error('Error canceling payment:', error);
            showAlert('결제 취소에 실패했습니다.', 'error');
        }
    };

    //날짜 데이터 형식 변환
    const formatDateTime = (date: Date | null) => {
        if(!date) return '-';
        return new Date(date).toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        })
    }

    //날짜 데이터 형식 변환
    const formatDate = (date: Date | null) => {
        if(!date) return '-';
        return new Date(date).toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        })
    }

    //결제 상태
    const paymentStatus = () => {
        if(!data) return '-';
        if(data?.canceledAt) return '납부(취소)';
        if(data?.paidAt) return '납부';
        return '미납';
    }

    //클라이언트 데이터
    const getClientInfo = () => {
        if (!data) return [];
        return [
            {
                label: "회사명",
                value: data.companyName
            },
            {
                label: "이메일",
                value: data.email
            },
            {
                label: "주문명",
                value: data?.orderName || "-"
            }
        ];
    };

    //결제 데이터
    const getPaymentInfo = () => {
        if (!data) return [];

        const items = [
            {
                label: "결제 금액",
                value: `${data.amount.toLocaleString()}원`
            },
            {
                label: "결제 상태",
                value: paymentStatus()
            },
            {
                label: "결제 방식",
                value: data?.paymentType || "-"
            },
            {
                label: "결제 키",
                value: data?.paymentKey || "-"
            }
        ];

        // 카드 결제일 경우 카드번호 출력
        if (data.cardNumber) {
            items.push({
                label: "카드번호",
                value: data.cardNumber
            });
        }
        //취소된 결제일 경우
        if (data.canceledAt) {
            //취소금액 출력
            items.push({
                label: "취소금액",
                value: data.cancelAmount ? data.cancelAmount.toLocaleString() + "원" : "-"
            });
        }

        return items;
    };

    //날짜 데이터
    const getDateInfo = () => {
        if (!data) return [];

        const items = [
            {
                label: "결제 예정일",
                value: formatDate(data?.dueAt || null)
            },
            {
                label: "실제 결제일",
                value: formatDateTime(data?.paidAt || null)
            }
        ];

        //취소된 결제일 경우
        if (data.canceledAt) {
            //취소일시 출력
            items.push({
                label: "취소일시",
                value: formatDateTime(data?.canceledAt || null)
            });
        }

        return items;
    };

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    if (!data) {
        return <div className={styles.error}>결제 정보를 찾을 수 없습니다.</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>결제 상세 정보</h2>
                {/*취소되지 않은 결제일 경우 취소 버튼 출력*/}
                {data?.paidAt && !data?.canceledAt && (
                    <Button
                        variant="secondary"
                        onClick={handleCancelClick}
                        size="small"
                    >
                        결제 취소
                    </Button>
                )}
            </div>
            <div className={styles.text}>
                {/*결제 정보*/}
                <TextCard items={getClientInfo()} className={styles.fullWidth}/>
                <TextCard items={getPaymentInfo()} className={styles.fullWidth}/>
                <TextCard items={getDateInfo()} className={styles.fullWidth}/>
            </div>
            {/*결제 취소 팝업*/}
            <CancelPaymentPopUp
                isOpen={isCancelModalOpen}
                onClose={() => setIsCancelModalOpen(false)}
                onConfirm={handleCancelConfirm}
                totalAmount={data.amount}
            />
        </div>
    );
};

export default AdminBillingInfo;